import middleware from "../../../shared/middleware";
import { trackGenericAction } from "../../../shared/analytics/dataLayer";
import { getCustomContextMetadata, trackConvivaCustomEvent } from "../../../shared/analytics/media";
import { getAVSPlaybackError, getPlaybackErrorMessageInfo } from "../../../shared/utils/playbackHelpers";
import { getPPVTuneInfo } from "../../../shared/utils/playerUtils";
import telusConvivaAnalytics from "../../../shared/utils/convivaAnalytics";
import { isPinMaxLimitReach, isBookmarkComplete } from "../../../shared/utils";
import { getGenericErrorEventHandler } from "../../../shared/analytics/helpers";
import constants from "../../../shared/constants";
import { PIN_MODAL_MODES, PIN_MODAL_TYPES } from "../../../components/PinModal";
import PlayerConstants from "../../../shared/constants/player";
import errorConstants from "../../../shared/constants/error";
import { ACTION_VALUES, ANALYTICS_EVENT_TYPES, WEB_ACTION_EVENT_NAMES } from "../../../shared/constants/analytics";
const { ERROR_TYPES, AVS_ERROR_CODES } = errorConstants;
const { MODAL_TYPES, MVE2_AGL_VERSION } = constants;
const { ASSET_TYPES, PLAYBACK_TYPES } = PlayerConstants;

const { fetchVideoUrl, getContentUserData } = middleware;

export const fetchStream = async ({
  assetId,
  parentalPin,
  tokenOnly = false,
  currentAssetId,
  isLivePlayer,
  currentChannelId,
  currentEpisodeId,
  appProvider,
  contentType,
  cancelTokenSource,
  pcPin,
  pcPinTimer,
  restartPcPinTimer,
  getMediaNodeMetaData,
  isConvivaAppTrackerEnabled,
  userProfile,
  isInHome,
  setVideoUrl,
  showPlaybackError,
  setAuthToken,
  setHasChannelTuneInfo,
  setLivePlaybackCanStart,
  translate,
  history,
  showModalPopup,
  dispatch,
  setFatalPlaybackError,
  isDynatraceTrackingEnabled,
}) => {
  currentAssetId.current = assetId;
  const contentId = isLivePlayer ? currentChannelId : currentEpisodeId;

  try {
    const res = await fetchVideoUrl(
      appProvider,
      contentType,
      contentId,
      assetId,
      parentalPin,
      tokenOnly,
      cancelTokenSource.current
    );

    if (!tokenOnly) {
      if (pcPin.current === null) {
        pcPin.current = parentalPin;

        // User's successful pin entry is valid for a period of time OR until they close the player
        if (!pcPinTimer.current) {
          restartPcPinTimer();
        }

        // parental_pin_content_unlock should only be called when there was a modal popup,
        // without checking `pcPin.current === null` content_unlock event will be triggered multiple times if there was an error occurred
        if (parentalPin) {
          trackGenericAction(
            ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK,
            getMediaNodeMetaData(),
            isDynatraceTrackingEnabled
          );
          if (isConvivaAppTrackerEnabled) {
            trackConvivaCustomEvent(
              ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK,
              getCustomContextMetadata(getMediaNodeMetaData(), userProfile, appProvider, isInHome)
            );
          }
        }
      }
      setVideoUrl(res.src);
      dispatch(showPlaybackError(null, null));
    }
    setAuthToken(res.token);
    if (isLivePlayer) {
      setHasChannelTuneInfo(true);
      setLivePlaybackCanStart(true);
    }
  } catch (err) {
    if (
      parentalPin &&
      (appProvider.AGL_Version === MVE2_AGL_VERSION
        ? err?.code?.includes(AVS_ERROR_CODES.USER_NO_RIGHTS)
        : AVS_ERROR_CODES.INCORRECT_PIN_CODES.includes(err?.code))
    ) {
      telusConvivaAnalytics.reportParentalPinFailed(false, err);
      throw err;
    } else if (parentalPin && isPinMaxLimitReach(err)) {
      telusConvivaAnalytics.reportParentalPinFailed(true, err);
      throw err;
    } else if (err?.code === AVS_ERROR_CODES.PANIC_MODE || err?.code === AVS_ERROR_CODES.PC_LEVEL_BLOCKED_DURING_ECE) {
      telusConvivaAnalytics.reportPlaybackError(err);

      const modalContent = {
        title: translate("programming_unavailable"),
        history,
        isCloseable: true,
        message: translate("programming_unavailable_body")?.replace("%s", appProvider.pcLevelLabel),
      };

      dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
    } else {
      telusConvivaAnalytics.reportPlaybackError(err);

      console.error(err);
      const AVSError = getAVSPlaybackError(err);
      setFatalPlaybackError(AVSError);
      dispatch(showPlaybackError(AVSError.code, AVSError.message));
    }
  }
};

export const handleStreamChange = async ({
  playbackType,
  isReadyToChangeStreams,
  fatalPlaybackError,
  isLivePlayer,
  currentChannelId,
  currentEpisodeId,
  prevContentIdForApiRef,
  isPPV,
  ppvPurchasePackage,
  prevPurchasePackageForApiRef,
  useRoutePlaybackState,
  appProvider,
  contentType,
  cancelTokenSource,
  restartAssetId,
  setPpvPurchasePackage,
  setAssetId,
  setBookmark,
  setProgressTime,
  fetchStream,
  showModalPopup,
  dispatch,
  getMediaNodeMetaData,
  translate,
  history,
  setIsCallParentalPinLock,
  pcPin,
  nextProgramInfo,
  isDynatraceTrackingEnabled,
}) => {
  if (isReadyToChangeStreams && !fatalPlaybackError) {
    const contentId = isLivePlayer ? currentChannelId : currentEpisodeId;
    if (
      contentId &&
      (contentId !== prevContentIdForApiRef.current ||
        (isPPV && ppvPurchasePackage !== prevPurchasePackageForApiRef.current))
    ) {
      prevContentIdForApiRef.current = contentId;
      if (!useRoutePlaybackState.current) {
        try {
          const res = await getContentUserData(
            appProvider,
            contentId,
            contentType,
            cancelTokenSource.current,
            restartAssetId.current
          );
          if (res?.containers?.length > 0) {
            const contentUserData = res.containers[0];
            const entitlement = contentUserData.entitlement;
            if (entitlement?.assets) {
              let assetToPlay;
              // TCDWC-1474 TODO: determine if condition needs update to include isPPV check when tune logic is consolidated
              if (isLivePlayer) {
                // PPV logic
                const ppvTuneInfo = getPPVTuneInfo(entitlement);
                if (ppvTuneInfo) {
                  assetToPlay = ppvTuneInfo.assetToPlay;
                  setPpvPurchasePackage(ppvTuneInfo.purchasePackage);
                  prevPurchasePackageForApiRef.current = ppvTuneInfo.purchasePackage;
                }
              } else {
                // VOD and recording playback logic
                if (playbackType === PLAYBACK_TYPES.TRAILER) {
                  assetToPlay = entitlement.assets.find(
                    (asset) => asset.assetType.toLowerCase() === ASSET_TYPES.TRAILER
                  );
                } else {
                  assetToPlay = entitlement.assets.find(
                    (asset) => asset.assetType.toLowerCase() === ASSET_TYPES.MASTER
                  );
                }

                let skipTimeSeconds = 0;
                if (contentUserData.metadata?.bookmarks?.length > 0) {
                  // BE always pushes the newest bookmark at the front of the array on the json.
                  // Fetching at element of 0 ensures the latest bookmark
                  const bookmark = contentUserData.metadata.bookmarks[0];
                  if (isBookmarkComplete(bookmark)) {
                    setBookmark(null);
                  } else {
                    skipTimeSeconds = bookmark.startDeltaTime;
                    setBookmark(bookmark);
                  }
                } else {
                  setBookmark(null);
                }
                setProgressTime(skipTimeSeconds);
              }

              if (assetToPlay) {
                setAssetId(assetToPlay.assetId);
                //checks before initiating playback
                if (
                  !entitlement.isPCBlocked &&
                  !entitlement.isContentOOHBlocked &&
                  !entitlement.isGeofencedBlocked &&
                  !entitlement.isSportBlackoutBlocked &&
                  !entitlement.isPlatformBlacklisted &&
                  !entitlement.isChannelNotSubscribed &&
                  !entitlement.isGeoBlocked
                ) {
                  fetchStream(assetToPlay.assetId);
                } else {
                  let modalContent = {
                    title: ERROR_TYPES.INFO,
                    history,
                    isCloseable: true,
                    analyticsInfo: {
                      errorCode: res.errorDescription,
                      media: getMediaNodeMetaData(),
                    },
                  };
                  let modalType = MODAL_TYPES.ERROR;

                  if (entitlement.isPCBlocked) {
                    if (pcPin.current && !nextProgramInfo) {
                      modalContent = null;
                      fetchStream(assetToPlay.assetId, pcPin.current);
                    } else {
                      telusConvivaAnalytics.reportParentalPinStart(assetToPlay.assetName, contentType);
                      modalType = MODAL_TYPES.PIN;
                      Object.assign(modalContent, {
                        pinModalMode: PIN_MODAL_MODES.ACCESS,
                        pinModalType: PIN_MODAL_TYPES.PARENTAL,
                        pinConfirmHandler: (pin) => {
                          return fetchStream(assetToPlay.assetId, pin);
                        },
                        pinAnalyticsErrorEventHandler: getGenericErrorEventHandler(
                          ANALYTICS_EVENT_TYPES.PARENTAL_PIN_CONTENT_UNLOCK_ERROR,
                          ACTION_VALUES.PARENTAL_PIN_CONTENT_UNLOCK,
                          WEB_ACTION_EVENT_NAMES.PARENTAL_PIN_CONTENT_UNLOCK_ERROR,
                          isDynatraceTrackingEnabled
                        ),
                      });

                      modalContent.title = translate("enter_parental_pin");
                      setIsCallParentalPinLock(true);
                    }
                  } else {
                    if (entitlement.isContentOOHBlocked) {
                      modalContent.message = translate("message_in_home");
                    } else if (entitlement.isGeofencedBlocked) {
                      modalContent.message = translate("error_outside_region");
                    } else if (entitlement.isSportBlackoutBlocked) {
                      modalContent.message = translate("error_program_restricted");
                    } else if (entitlement.isPlatformBlacklisted) {
                      modalContent.message = translate("error_program_not_available_device");
                    } else if (entitlement.isChannelNotSubscribed) {
                      modalContent.message = translate("message_subscribe");
                    } else if (entitlement.isGeoBlocked) {
                      modalContent.message = translate("error_program_not_available_region");
                    }
                  }

                  if (modalContent) {
                    dispatch(showModalPopup(modalType, modalContent));
                  }
                }
              }
            }
          } else if (res.errorDescription) {
            const { errorTitle, errorMessage } = getPlaybackErrorMessageInfo({ code: res.errorDescription });
            const message = errorMessage ? translate(errorMessage) : "";
            const errorDetails = errorTitle ? translate(errorTitle) : "";
            const modalContent = {
              title: errorDetails,
              history,
              isCloseable: true,
              message,
              analyticsInfo: {
                errorCode: res.errorDescription,
                media: getMediaNodeMetaData(),
              },
            };

            dispatch(showModalPopup(MODAL_TYPES.ERROR, modalContent));
          }
        } catch (err) {
          console.error(err);
        }
      }
    }
  }
};
